<template>
    <div class="question-lists-dashboard animation-fadeIn">
        <!-- Page Header -->
        <div class="page-header-container">
            <div class="page-header">
                <div class="header-info">
                    <h2 class="page-title">精选题单</h2>
                    <p class="page-subtitle">探索高质量专题题单，快速定位核心考点，一键开启高效组卷</p>
                </div>
                <div class="header-actions">
                    <el-button type="primary" size="large" class="create-btn" @click="showCreateDialog = true">
                        <el-icon>
                            <Plus />
                        </el-icon> 创建新题单
                    </el-button>
                </div>
            </div>
        </div>

        <div class="content-body">
            <!-- Tab Selection & Search -->
            <div class="list-controls">
                <el-tabs v-model="activeTab" @tab-change="handleTabChange" class="modern-tabs">
                    <el-tab-pane label="官方精选" name="official"></el-tab-pane>
                    <el-tab-pane label="用户分享" name="shared"></el-tab-pane>
                    <el-tab-pane label="我的收藏" name="favorite"></el-tab-pane>
                </el-tabs>

                <div class="control-spacer"></div>

                <div class="search-box">
                    <el-input v-model="keyword" placeholder="在该领域搜索..." clearable class="custom-search">
                        <template #prefix>
                            <el-icon>
                                <Search />
                            </el-icon>
                        </template>
                    </el-input>
                </div>
            </div>

            <!-- Lists Grid -->
            <div class="lists-grid" v-loading="loading">
                <el-empty v-if="filteredLists.length === 0 && !loading" description="空空如也，暂无相关题单" />

                <div v-for="list in filteredLists" :key="list.id" class="list-card-wrapper"
                    @click="goToListDetail(list)">
                    <div class="list-modern-card">
                        <div class="l-card-bg"></div>
                        <div class="l-card-content">
                            <div class="l-card-header">
                                <div v-if="list.isDefault" class="l-status-badge is-default"
                                    style="background: #e6f7ff; color: #1890ff;">
                                    默认题库
                                </div>
                                <div v-else class="l-status-badge" :class="{ 'is-public': list.isPublic }">
                                    {{ list.isPublic ? 'PUBLIC' : 'PRIVATE' }}
                                </div>
                                <span class="l-date">{{ formatDate(list.createdAt) }}</span>
                                <div v-if="list.isFeatured" class="l-featured-badge">
                                    <el-icon>
                                        <StarFilled />
                                    </el-icon> 精选
                                </div>
                            </div>

                            <div class="l-card-main">
                                <h3 class="l-title" :title="list.title">{{ list.title }}</h3>
                                <p class="l-desc">{{ list.description || '暂无详细描述信息' }}</p>

                                <div class="l-stats">
                                    <div class="stat-item">
                                        <el-icon>
                                            <Document />
                                        </el-icon>
                                        <span class="stat-value">{{ list.questions?.length || 0 }}</span>
                                        <span class="stat-label">试题</span>
                                    </div>
                                </div>
                            </div>

                            <div class="l-card-footer">
                                <div class="l-author">
                                    <el-avatar :size="28"
                                        :src="'https://api.dicebear.com/7.x/avataaars/svg?seed=' + list.userId" />
                                    <span class="author-name">{{ list.userId === currentUserId ? '我' : '优质创作者' }}</span>
                                </div>
                                <div class="l-footer-actions">
                                    <el-button v-if="isAdmin" :type="list.isFeatured ? 'warning' : 'info'" link
                                        class="featured-btn" @click.stop="toggleFeatured(list)">
                                        <el-icon>
                                            <component :is="list.isFeatured ? 'StarFilled' : 'Star'" />
                                        </el-icon>
                                    </el-button>
                                    <el-button :type="list.isFavorite ? 'danger' : 'info'" link class="fav-btn"
                                        @click.stop="toggleFavorite(list)">
                                        <el-icon>
                                            <component :is="list.isFavorite ? 'StarFilled' : 'Star'" />
                                        </el-icon>
                                    </el-button>
                                    <el-button type="warning" link class="download-btn"
                                        @click.stop="downloadList(list)">
                                        <el-icon>
                                            <Download />
                                        </el-icon>
                                    </el-button>
                                    <el-button type="primary" link class="view-btn">
                                        详情 <el-icon>
                                            <ArrowRight />
                                        </el-icon>
                                    </el-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create List Dialog -->
        <el-dialog v-model="showCreateDialog" title="创建新题单" width="500px">
            <el-form :model="createForm" label-position="top">
                <el-form-item label="题单标题" required>
                    <el-input v-model="createForm.title" placeholder="例如：圆锥曲线中的定点问题专题" />
                </el-form-item>
                <el-form-item label="题单描述">
                    <el-input v-model="createForm.description" type="textarea" :rows="3"
                        placeholder="简要介绍这个题单包含的内容..." />
                </el-form-item>
                <el-form-item label="公开展示">
                    <el-switch v-model="createForm.isPublic" active-text="允许他人查看此题单" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showCreateDialog = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="handleCreateList">立即创建</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Plus, Search, Document, ArrowRight, UserFilled, Download, Star, StarFilled } from '@element-plus/icons-vue'
import api, { getUser } from '@/utils/api'

const router = useRouter()
const loading = ref(false)
const saving = ref(false)
const lists = ref([])
const activeTab = ref('official')
const keyword = ref('')
const showCreateDialog = ref(false)

const currentUserId = computed(() => getUser()?.id)
const isAdmin = computed(() => {
    const user = getUser()
    return user && (user.role === 'admin' || user.isAdmin)
})

const createForm = reactive({
    title: '',
    description: '',
    isPublic: false
})

const filteredLists = computed(() => {
    let result = lists.value
    if (keyword.value) {
        const k = keyword.value.toLowerCase()
        result = result.filter(l => l.title.toLowerCase().includes(k) || l.description?.toLowerCase().includes(k))
    }
    return result
})

const fetchLists = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/question-lists', {
            params: { type: activeTab.value }
        })
        lists.value = res.data.lists || []
    } catch (err) {
        ElMessage.error('加载题单列表失败')
    } finally {
        loading.value = false
    }
}

const handleTabChange = () => {
    fetchLists()
}

const handleCreateList = async () => {
    if (!createForm.title.trim()) {
        return ElMessage.warning('请输入题单标题')
    }

    saving.value = true
    try {
        await api.post('/api/question-lists', createForm)
        ElMessage.success('题单创建成功')
        showCreateDialog.value = false
        createForm.title = ''
        createForm.description = ''
        createForm.isPublic = false
        fetchLists()
    } catch (err) {
        ElMessage.error('创建失败')
    } finally {
        saving.value = false
    }
}

const goToListDetail = (list) => {
    router.push(`/question-lists/${list.id}`)
}

const downloadList = (list) => {
    if (list.id) {
        window.open(`/api/question-lists/${list.id}/download`, '_blank')
    }
}

const toggleFeatured = async (list) => {
    try {
        await api.put(`/api/question-lists/${list.id}`, {
            ...list,
            isFeatured: !list.isFeatured
        })
        list.isFeatured = !list.isFeatured
        ElMessage.success(list.isFeatured ? '已设为精选' : '已取消精选')
    } catch (e) {
        ElMessage.error('操作失败')
    }
}

const toggleFavorite = async (list) => {
    try {
        const res = await api.post(`/api/question-lists/${list.id}/favorite`)
        list.isFavorite = res.data.isFavorite
        if (list.isFavorite) {
            ElMessage.success('收藏成功')
        } else {
            ElMessage.success('已取消收藏')
            if (activeTab.value === 'favorite') {
                fetchLists() // Refresh to remove from list
            }
        }
    } catch (e) {
        ElMessage.error('操作失败')
    }
}

const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

onMounted(() => {
    fetchLists()
})
</script>

<style scoped>
.question-lists-dashboard {
    min-height: 100vh;
    background-color: #f8fafc;
}

.page-header-container {
    background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
    padding: 60px 40px;
    margin-bottom: -40px;
}

.page-header {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 36px;
    font-weight: 800;
    color: #ffffff;
    margin: 0;
    letter-spacing: -0.025em;
}

.page-subtitle {
    font-size: 16px;
    color: #bfdbfe;
    margin-top: 12px;
}

.create-btn {
    height: 48px;
    padding: 0 24px;
    font-weight: 700;
    border-radius: 12px;
    background: #ffffff;
    color: #2563eb;
    border: 2px solid #ffffff;
    box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.2);
    transition: all 0.3s;
}

.create-btn:hover {
    transform: translateY(-2px);
    background: #f8fafc;
    box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.3);
    color: #1e40af;
}

.content-body {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 40px 40px;
}

.list-controls {
    background: #ffffff;
    padding: 20px 30px;
    border-radius: 20px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    margin-bottom: 32px;
}

.modern-tabs :deep(.el-tabs__header) {
    margin: 0;
    border: none;
}

.modern-tabs :deep(.el-tabs__nav-wrap::after) {
    display: none;
}

.modern-tabs :deep(.el-tabs__item) {
    font-size: 16px;
    font-weight: 600;
    color: #64748b;
    padding: 0 24px;
    height: 44px;
}

.modern-tabs :deep(.el-tabs__item.is-active) {
    color: #2563eb;
}

.custom-search :deep(.el-input__wrapper) {
    border-radius: 12px;
    background-color: #f1f5f9;
    box-shadow: none !important;
    padding: 8px 16px;
    width: 320px;
}

.lists-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 28px;
}

.list-card-wrapper {
    perspective: 1000px;
    cursor: pointer;
}

.list-modern-card {
    position: relative;
    background: #ffffff;
    border-radius: 24px;
    overflow: hidden;
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid #f1f5f9;
    height: 280px;
}

.l-card-bg {
    position: absolute;
    top: -50px;
    right: -50px;
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%);
    border-radius: 50%;
    transition: all 0.5s;
}

.list-card-wrapper:hover .list-modern-card {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 25px 40px -10px rgba(0, 0, 0, 0.08);
}

.list-card-wrapper:hover .l-card-bg {
    transform: scale(1.5);
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
}

.l-card-content {
    position: relative;
    z-index: 1;
    padding: 28px;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.l-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.l-status-badge {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.05em;
    padding: 4px 10px;
    border-radius: 6px;
    background: #f1f5f9;
    color: #64748b;
}

.l-status-badge.is-public {
    background: #dbeafe;
    color: #2563eb;
}

.l-date {
    font-size: 12px;
    font-weight: 500;
    color: #94a3b8;
}

.l-card-main {
    flex: 1;
}

.l-title {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 12px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.l-desc {
    font-size: 14px;
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 20px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    height: 44px;
}

.l-stats {
    display: flex;
    gap: 20px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f8fafc;
    padding: 6px 12px;
    border-radius: 10px;
}

.stat-item .el-icon {
    font-size: 14px;
    color: #3b82f6;
}

.stat-value {
    font-weight: 700;
    font-size: 15px;
    color: #1e293b;
}

.stat-label {
    font-size: 12px;
    color: #64748b;
}

.l-card-footer {
    border-top: 1px solid #f1f5f9;
    padding-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.l-author {
    display: flex;
    align-items: center;
    gap: 10px;
}

.author-name {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.l-footer-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.download-btn {
    padding: 0;
    font-size: 18px;
    color: #e6a23c !important;
}

.download-btn:hover {
    transform: scale(1.1);
}

.view-btn {
    font-weight: 600;
    font-size: 14px;
    padding: 0;
}

.view-btn:hover {
    padding-left: 4px;
}

.l-featured-badge {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 700;
    color: #e6a23c;
    background: #fdf6ec;
    padding: 2px 8px;
    border-radius: 4px;
}

.featured-btn {
    padding: 0;
    font-size: 18px;
}

.fav-btn {
    padding: 0;
    font-size: 18px;
}

.fav-btn:hover {
    color: #f56c6c;
    transform: scale(1.1);
}

.animation-fadeIn {
    animation: fadeIn 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 768px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 20px;
    }

    .list-controls {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
    }

    .custom-search :deep(.el-input__wrapper) {
        width: 100%;
    }
}
</style>
